import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EventFormModal = ({ isOpen, onClose, onSave, evento, empleados }) => {
  const [formData, setFormData] = useState({
    fecha: '',
    horaInicio: '',
    horaFin: '',
    cliente: '',
    ubicacion: '',
    tipoServicio: 'EVENTO',
    empleadoId: '',
    numPiezas: 0,
    descripcionPiezas: '',
    precioServicio: '',
    gastos: '',
    observaciones: '',
    estado: 'PROGRAMADO'
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (evento) {
      setFormData({
        fecha: evento?.fecha || '',
        horaInicio: evento?.horaInicio || '',
        horaFin: evento?.horaFin || '',
        cliente: evento?.cliente || '',
        ubicacion: evento?.ubicacion || '',
        tipoServicio: evento?.tipoServicio || 'EVENTO',
        empleadoId: evento?.empleadoId || '',
        numPiezas: evento?.numPiezas || 0,
        descripcionPiezas: evento?.descripcionPiezas || '',
        precioServicio: evento?.precioServicio || '',
        gastos: evento?.gastos || '',
        observaciones: evento?.observaciones || '',
        estado: evento?.estado || 'PROGRAMADO'
      });
    } else {
      setFormData({
        fecha: '',
        horaInicio: '',
        horaFin: '',
        cliente: '',
        ubicacion: '',
        tipoServicio: 'EVENTO',
        empleadoId: '',
        numPiezas: 0,
        descripcionPiezas: '',
        precioServicio: '',
        gastos: '',
        observaciones: '',
        estado: 'PROGRAMADO'
      });
    }
    setErrors({});
  }, [evento, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData?.fecha) newErrors.fecha = 'La fecha es requerida';
    if (!formData?.cliente?.trim()) newErrors.cliente = 'El cliente es requerido';
    if (!formData?.tipoServicio) newErrors.tipoServicio = 'El tipo de servicio es requerido';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      setErrors({ submit: error?.message || 'Error al guardar el evento' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const tipoServicioOptions = [
    { value: 'EVENTO', label: 'Evento' },
    { value: 'TIENDA', label: 'Tienda' },
    { value: 'FERIA', label: 'Feria' },
    { value: 'PRIVADO', label: 'Privado' }
  ];

  const estadoOptions = [
    { value: 'PROGRAMADO', label: 'Programado' },
    { value: 'REALIZADO', label: 'Realizado' },
    { value: 'CANCELADO', label: 'Cancelado' }
  ];

  const empleadoOptions = [
    { value: '', label: 'Sin asignar' },
    ...(empleados?.map(e => ({ value: e?.id, label: e?.nombre })) || [])
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
            {evento ? 'Editar Evento' : 'Nuevo Evento'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Fecha"
              required
              value={formData?.fecha}
              onChange={(e) => handleChange('fecha', e?.target?.value)}
              error={errors?.fecha}
            />

            <Input
              type="time"
              label="Hora de inicio"
              value={formData?.horaInicio}
              onChange={(e) => handleChange('horaInicio', e?.target?.value)}
            />

            <Input
              type="time"
              label="Hora de fin"
              value={formData?.horaFin}
              onChange={(e) => handleChange('horaFin', e?.target?.value)}
            />

            <Input
              type="text"
              label="Cliente"
              required
              value={formData?.cliente}
              onChange={(e) => handleChange('cliente', e?.target?.value)}
              error={errors?.cliente}
              placeholder="Nombre del cliente"
            />
          </div>

          <Input
            type="text"
            label="Ubicación"
            value={formData?.ubicacion}
            onChange={(e) => handleChange('ubicacion', e?.target?.value)}
            placeholder="Dirección o lugar del evento"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Tipo de servicio"
              required
              options={tipoServicioOptions}
              value={formData?.tipoServicio}
              onChange={(value) => handleChange('tipoServicio', value)}
              error={errors?.tipoServicio}
            />

            <Select
              label="Estado"
              options={estadoOptions}
              value={formData?.estado}
              onChange={(value) => handleChange('estado', value)}
            />
          </div>

          <Select
            label="Empleado asignado"
            options={empleadoOptions}
            value={formData?.empleadoId}
            onChange={(value) => handleChange('empleadoId', value)}
            searchable
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              label="Número de piezas"
              value={formData?.numPiezas}
              onChange={(e) => handleChange('numPiezas', parseInt(e?.target?.value) || 0)}
              min="0"
            />

            <Input
              type="text"
              label="Descripción de piezas"
              value={formData?.descripcionPiezas}
              onChange={(e) => handleChange('descripcionPiezas', e?.target?.value)}
              placeholder="Ej: Jamón ibérico, paletilla..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="number"
              label="Precio del servicio (€)"
              value={formData?.precioServicio}
              onChange={(e) => handleChange('precioServicio', e?.target?.value)}
              step="0.01"
              min="0"
              placeholder="0.00"
            />

            <Input
              type="number"
              label="Gastos (€)"
              value={formData?.gastos}
              onChange={(e) => handleChange('gastos', e?.target?.value)}
              step="0.01"
              min="0"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="text-base font-semibold font-oranienbaum leading-none text-foreground mb-2 block">
              Observaciones
            </label>
            <textarea
              value={formData?.observaciones}
              onChange={(e) => handleChange('observaciones', e?.target?.value)}
              className="flex min-h-[100px] w-full rounded-md border border-border bg-input px-4 py-3 text-lg font-oranienbaum text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Notas adicionales sobre el evento..."
            />
          </div>

          {errors?.submit && (
            <div className="p-3 bg-error/10 border border-error rounded-md">
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1"
              loading={loading}
              disabled={loading}
            >
              {evento ? 'Guardar cambios' : 'Crear evento'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventFormModal;